import { useEffect, useMemo, useState } from "react"
import { SpinnerText } from "./Spinner"
import { useSelector } from "react-redux"
import { FiDownload, FiX } from "react-icons/fi"
import { getApplicationDrafts } from "../../services/applicationService"
import { getDesignApplicationDrafts } from "../../services/designApplicationService"
import { RootState } from "../../store/store"

type ImportSource = "trademark" | "design"
type ImportTarget = "goods" | "applicants"

type PriorApplicationImportButtonProps = {
  source: ImportSource
  target: ImportTarget
  currentApplicationId?: string | null
  ownerUserId?: string | null
  disabled?: boolean
  hasCurrentData?: boolean
  mode?: "replace" | "append"
  onImport: (value: any[]) => boolean | void
}

function getEntityId(value: any) {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") {
    return String(value._id || value.id || "")
  }
  return String(value)
}

function getApplicationOwnerId(application: any) {
  return getEntityId(application.userId || application.user)
}

const allowedApplicantFields = [
  "type",
  "euipoPersonIdentifier",
  "firstName",
  "lastName",
  "nationality",
  "legalName",
  "nationalDesignation",
  "companySize",
  "activitySector",
  "countryOfIncorporation",
  "countryOfResidence",
  "organisationName",
  "department",
  "commonRepresentative",
  "isAlsoInventor",
  "address",
  "serviceAddress",
  "employeeRepresentative",
  "contact"
]

function cleanImportedRecord(record: any) {
  if (!record || typeof record !== "object") return record

  return allowedApplicantFields.reduce((copy: any, field) => {
    if (record[field] !== undefined) {
      copy[field] = JSON.parse(JSON.stringify(record[field]))
    }
    return copy
  }, {})
}

function extractGoodsAndServices(application: any) {
  const entries = application.goodsAndServices?.entries
  if (!Array.isArray(entries)) return []

  return entries.flatMap((entry: any) =>
    Array.isArray(entry.terms)
      ? entry.terms.map((term: string) => ({
          classNumber: entry.classNumber,
          text: term
        }))
      : []
  )
}

function extractApplicants(application: any) {
  return Array.isArray(application.applicants)
    ? application.applicants.map(cleanImportedRecord)
    : []
}

function getReference(application: any) {
  return application.referenceNumber?.referenceNumber || "-"
}

function getImportItems(application: any, target: ImportTarget) {
  return target === "goods"
    ? extractGoodsAndServices(application)
    : extractApplicants(application)
}

function getApplicantName(applicant: any) {
  if (applicant.type === "legal") return applicant.legalName || applicant.organisationName || "Legal entity"
  return [applicant.firstName, applicant.lastName].filter(Boolean).join(" ") || applicant.name || "Natural person"
}

function getItemCountLabel(application: any, target: ImportTarget) {
  const count = getImportItems(application, target).length
  if (target === "goods") return `${count} terms`
  return `${count} applicants`
}

function getApplicantPreview(application: any) {
  const applicants = extractApplicants(application)
  if (applicants.length === 0) return ""
  const names = applicants.map(getApplicantName).filter(Boolean)
  const visibleNames = names.slice(0, 3).join(", ")
  return names.length > 3 ? `${visibleNames} +${names.length - 3} more` : visibleNames
}

function getGoodsClassPreview(application: any) {
  const classNumbers = Array.from(
    new Set(
      extractGoodsAndServices(application)
        .map((item: any) => item.classNumber)
        .filter((classNumber: any) => classNumber !== undefined && classNumber !== null)
    )
  ).sort((a: any, b: any) => Number(a) - Number(b))

  if (classNumbers.length === 0) return ""
  return `Classes: ${classNumbers.join(", ")}`
}

function getButtonLabel(source: ImportSource) {
  return source === "trademark"
    ? "Import from prior trademark application"
    : "Import from prior design application"
}

export default function PriorApplicationImportButton({
  source,
  target,
  currentApplicationId,
  ownerUserId,
  disabled = false,
  hasCurrentData = false,
  mode = target === "applicants" ? "append" : "replace",
  onImport
}: PriorApplicationImportButtonProps) {
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id)
  const effectiveOwnerUserId = ownerUserId || currentUserId || ""
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [applications, setApplications] = useState<any[]>([])
  const [error, setError] = useState("")
  const [info, setInfo] = useState("")
  const [pendingApplication, setPendingApplication] = useState<any | null>(null)

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setError("")
    setInfo("")
    setPendingApplication(null)

    const loadApplications = source === "trademark"
      ? getApplicationDrafts
      : getDesignApplicationDrafts

    loadApplications()
      .then((data) => {
        if (!cancelled) setApplications(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setError("Prior applications could not be loaded.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, source])

  const importableApplications = useMemo(() => {
    return applications
      .filter((application) => application._id !== currentApplicationId)
      .filter((application) => Boolean(effectiveOwnerUserId) && getApplicationOwnerId(application) === effectiveOwnerUserId)
      .filter((application) => getImportItems(application, target).length > 0)
  }, [applications, currentApplicationId, effectiveOwnerUserId, target])

  function closeModal() {
    setOpen(false)
    setInfo("")
    setPendingApplication(null)
  }

  function executeImport(application: any) {
    setInfo("")
    const imported = onImport(getImportItems(application, target))

    if (imported === false) {
      setInfo(
        target === "applicants"
          ? "All applicants are already present."
          : "Nothing new could be imported."
      )
      return
    }

    closeModal()
  }

  function handleImport(application: any) {
    if (hasCurrentData && mode === "replace") {
      setPendingApplication(application)
      return
    }

    executeImport(application)
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-dantzBlue bg-white px-3 py-2 text-sm font-medium text-dantzBlue transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiDownload className="h-4 w-4" aria-hidden="true" />
        {getButtonLabel(source)}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {getButtonLabel(source)}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select the prior application whose data you want to import.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {loading && <p className="text-sm text-gray-500"><SpinnerText>Loading prior applications...</SpinnerText></p>}
              {error && <p className="text-sm text-red-600">{error}</p>}
              {info && (
                <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  {info}
                </p>
              )}
              {!loading && !error && importableApplications.length === 0 && (
                <p className="text-sm text-gray-500">No importable prior applications found.</p>
              )}

              {!loading && !error && importableApplications.length > 0 && (
                <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                  {importableApplications.map((application) => (
                    <button
                      key={application._id}
                      type="button"
                      onClick={() => handleImport(application)}
                      className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-blue-50"
                    >
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">
                          {getReference(application)}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          Last updated: {application.updatedAt ? new Date(application.updatedAt).toLocaleDateString() : "-"}
                        </span>
                        {target === "applicants" && getApplicantPreview(application) && (
                          <span className="mt-1 block text-xs text-gray-600">
                            {getApplicantPreview(application)}
                          </span>
                        )}
                        {target === "goods" && getGoodsClassPreview(application) && (
                          <span className="mt-1 block text-xs text-gray-600">
                            {getGoodsClassPreview(application)}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 rounded-full bg-[#e6edf5] px-3 py-1 text-xs font-medium text-dantzBlue">
                        {getItemCountLabel(application, target)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {pendingApplication && (
              <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-700">
                  {target === "goods"
                    ? "This will replace the current goods and services."
                    : "This will replace the current applicants."}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingApplication(null)}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => executeImport(pendingApplication)}
                    className="rounded-md bg-dantzBlue px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-900"
                  >
                    Import
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
